import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ShieldCheck, CheckCircle2, Download, ArrowRight, MapPin, CalendarDays,
  Users, Mail, AlertTriangle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
const ET_LOGO_NAVY = "/brand/et-logo-navy.png";

const C = { ink: "#1A1A1A", sub: "#52525B", mute: "#A1A1AA", line: "#E4E4E7", bg: "#F9F8F6", surface: "#FFFFFF", navy: "#121B28", sand: "#F4EBD7", olive: "#8a9771" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const eur = (n, cur = "EUR") => new Intl.NumberFormat("en-IE", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtDate = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d || ""; } };

const Fonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Manrope:wght@300;400;500;600;700&display=swap');
    .et-serif{font-family:'Cormorant Garamond',Georgia,serif;}
    .et-sans{font-family:'Manrope',system-ui,-apple-system,sans-serif;}
  `}</style>
);

export default function PayLink() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const paypalFlag = sp.get("paypal");
  const orderId = sp.get("token"); // PayPal añade ?token={orderId}&PayerID=...

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [option, setOption] = useState("");
  const [custom, setCustom] = useState("");
  const [passenger, setPassenger] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [paid, setPaid] = useState(null);
  const [cancelled, setCancelled] = useState(paypalFlag === "cancel");
  const [shareId, setShareId] = useState(() => sessionStorage.getItem(`pay-share-${token}`) || "");
  const [tos, setTos] = useState(false);
  const [tiOpen, setTiOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await pub.get(`/pay/${token}`);
      setInfo(data);
      const sid = sessionStorage.getItem(`pay-share-${token}`) || "";
      const sh = (data.payer_shares || []).find((x) => x.id === sid);
      const first = ((sh ? sh.options : data.options) || []).find((o) => o.enabled);
      if (first) setOption(first.key);
    } catch (e) {
      setError(e?.response?.data?.detail || "No pudimos cargar esta página de pago.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Aviso al agente: link abierto / vuelta de PayPal sin pagar (una señal por carga de página).
  useEffect(() => {
    if (!token) return;
    const event = paypalFlag === "cancel" ? "cancel" : (paypalFlag === "return" ? null : "open");
    if (!event) return;
    pub.post(`/pay/${token}/visit`, { event, payer_share_id: sessionStorage.getItem(`pay-share-${token}`) || null, order_id: orderId || null }).catch(() => {});
    // eslint-disable-next-line
  }, [token, paypalFlag]);

  // Vuelta desde PayPal → capturar la orden.
  useEffect(() => {
    if (paypalFlag !== "return" || !orderId) return;
    let on = true;
    (async () => {
      setCapturing(true);
      try {
        const { data } = await pub.post(`/pay/${token}/capture-order/${orderId}`);
        if (!on) return;
        if (data.status === "COMPLETED") {
          setPaid({
            amount: Number(data.amount || data.capture?.amount || 0),
            currency: data.currency || "EUR",
            pay_in_full: !!data.pay_in_full,
            receiptUrl: data.receipt?.url ? `${BACKEND}${data.receipt.url}` : null,
            emailed: !!data.receipt?.emailed, recipient: data.receipt?.recipient || null,
          });
        } else {
          setError("No pudimos confirmar tu pago. Inténtalo de nuevo o contacta con nosotros.");
        }
      } catch (e) {
        if (on) setError("Ocurrió un problema al finalizar tu pago. Inténtalo de nuevo.");
      } finally { if (on) setCapturing(false); }
    })();
    return () => { on = false; };
    // eslint-disable-next-line
  }, [paypalFlag, orderId, token]);

  const shares = info?.payer_shares || [];
  const share = useMemo(() => shares.find((x) => x.id === shareId) || null, [shares, shareId]);
  const options = share ? share.options : (info?.options || []);
  const selected = useMemo(() => options.find((o) => o.key === option), [options, option]);
  const remaining = Number((share ? share.amount - share.paid : info?.remaining) || 0);
  const cur = info?.currency || "EUR";
  const customNum = Number(custom || 0);
  const amountDue = option === "custom" ? customNum : Number(selected?.amount || 0);
  const passengerOk = passenger.first_name.trim() && passenger.last_name.trim() && /\S+@\S+\.\S+/.test(passenger.email);
  const customOk = option !== "custom" || (customNum > 0 && customNum <= remaining + 0.01);
  const shareOk = shares.length === 0 || !!share;
  const canPay = !!option && passengerOk && customOk && shareOk && tos && amountDue > 0 && !submitting;

  const pickShare = (id) => {
    setShareId(id);
    sessionStorage.setItem(`pay-share-${token}`, id);
    const sh = shares.find((x) => x.id === id);
    const first = (sh?.options || []).find((o) => o.enabled);
    setOption(first ? first.key : "");
    if (sh) setPassenger((p) => ({ ...p, email: p.email || sh.email || "", first_name: p.first_name || sh.name.split(" ")[0] || "", last_name: p.last_name || sh.name.split(" ").slice(1).join(" ") }));
  };

  const submit = async () => {
    if (!canPay) return;
    setSubmitting(true);
    try {
      const body = { option, passenger, tos_accepted: tos, payer_share_id: shareId || null };
      if (option === "custom") body.custom_amount = customNum;
      const { data } = await pub.post(`/pay/${token}/create-order`, body);
      if (data.approve_url) { window.location.href = data.approve_url; return; }
      toast.error("No se pudo iniciar el pago con PayPal.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago.");
    } finally { setSubmitting(false); }
  };

  const setP = (k, v) => setPassenger((s) => ({ ...s, [k]: v }));

  // ---------- Estados de carga / error ----------
  if (loading) {
    return (
      <div className="et-sans flex min-h-screen items-center justify-center" style={{ backgroundColor: C.bg }}>
        <Fonts /><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.navy }} />
      </div>
    );
  }
  if (error && !info) {
    return (
      <div className="et-sans flex min-h-screen flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: C.bg }} data-testid="pay-error">
        <Fonts />
        <AlertTriangle className="mb-4 h-10 w-10" style={{ color: C.olive }} />
        <p className="max-w-md text-base font-light" style={{ color: C.ink }}>{error}</p>
      </div>
    );
  }

  const heroBg = imgUrl(info?.hero_image);
  const success = !!paid || (info?.sold && remaining <= 0);

  return (
    <div className="et-sans min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="pay-page">
      <Fonts />
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-white/90 backdrop-blur-md" style={{ borderColor: C.line }}>
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <img src={ET_LOGO_NAVY} alt="Espíritu Travel" className="h-6 w-auto" data-testid="pay-logo" />
          <span className="et-sans flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: C.mute }}>
            <ShieldCheck className="h-4 w-4" style={{ color: C.olive }} /> Secure payment
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        {/* Resumen del viaje */}
        <div className="mb-10">
          <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: C.mute }}>Payment for your journey</p>
          <h1 className="et-serif mt-2 text-4xl font-medium leading-tight md:text-5xl" data-testid="pay-title">{info?.trip_title || "Your trip"}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-light" style={{ color: C.sub }}>
            {info?.destination && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: C.mute }} />{info.destination}</span>}
            {info?.start_date && <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" style={{ color: C.mute }} />{fmtDate(info.start_date)}{info.end_date ? ` – ${fmtDate(info.end_date)}` : ""}</span>}
            {info?.num_travelers ? <span className="flex items-center gap-1.5"><Users className="h-4 w-4" style={{ color: C.mute }} />{info.num_travelers} {info.num_travelers === 1 ? "traveller" : "travellers"}</span> : null}
          </div>
        </div>

        {heroBg && (
          <div className="mb-10 overflow-hidden border" style={{ borderColor: C.line }}>
            <img src={heroBg} alt={info?.trip_title || ""} className="h-56 w-full object-cover md:h-72" data-testid="pay-hero" />
          </div>
        )}

        {/* Totales */}
        <div className="mb-10 grid grid-cols-2 gap-px overflow-hidden border sm:grid-cols-3" style={{ borderColor: C.line, backgroundColor: C.line }}>
          <div className="bg-white p-5">
            <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: C.mute }}>Trip total</p>
            <p className="et-serif mt-1 text-2xl font-medium" data-testid="pay-total">{eur(info?.total, cur)}</p>
          </div>
          <div className="bg-white p-5">
            <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: C.mute }}>Already paid</p>
            <p className="et-serif mt-1 text-2xl font-medium" data-testid="pay-paid">{eur(info?.paid, cur)}</p>
          </div>
          <div className="col-span-2 bg-white p-5 sm:col-span-1">
            <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: C.mute }}>Remaining</p>
            <p className="et-serif mt-1 text-2xl font-medium" data-testid="pay-remaining">{eur(remaining, cur)}</p>
          </div>
        </div>

        {/* ---------- Vuelta desde PayPal: capturando ---------- */}
        {capturing && (
          <div className="border bg-white p-10 text-center" style={{ borderColor: C.line }} data-testid="pay-capturing">
            <Loader2 className="mx-auto h-8 w-8 animate-spin" style={{ color: C.navy }} />
            <p className="et-sans mt-4 text-base font-light" style={{ color: C.sub }}>Confirming your payment…</p>
          </div>
        )}

        {/* ---------- Éxito ---------- */}
        {!capturing && success && (
          <div className="border bg-white p-10 text-center" style={{ borderColor: C.line }} data-testid="pay-success">
            <CheckCircle2 className="mx-auto mb-4 h-14 w-14" style={{ color: C.olive }} />
            <h2 className="et-serif text-3xl font-medium md:text-4xl">Payment received</h2>
            <p className="et-sans mx-auto mt-3 max-w-md text-base font-light" style={{ color: C.sub }}>
              Thank you! We've received your payment{paid ? ` of ${eur(paid.amount, paid.currency)}` : ""}
              {paid?.pay_in_full ? " — your trip is fully paid and confirmed." : ". Your booking is confirmed; any remaining balance is scheduled before departure."}
            </p>
            {paid?.emailed && (
              <p className="et-sans mx-auto mt-3 flex items-center justify-center gap-1.5 text-sm font-light" style={{ color: C.sub }} data-testid="pay-emailed">
                <Mail className="h-4 w-4" style={{ color: C.olive }} /> We've emailed your receipt{paid?.recipient ? ` to ${paid.recipient}` : ""}.
              </p>
            )}
            {paid?.receiptUrl && (
              <a href={paid.receiptUrl} target="_blank" rel="noreferrer" data-testid="pay-receipt-btn"
                className="et-sans mx-auto mt-6 inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white transition-colors hover:opacity-90" style={{ backgroundColor: C.navy }}>
                <Download className="h-5 w-5" /> Download receipt
              </a>
            )}
          </div>
        )}

        {!capturing && success && !info?.traveler_info_submitted && (
          <TravelerInfoForm token={token} numTravelers={info?.num_travelers} open={tiOpen} setOpen={setTiOpen} onDone={load} />
        )}
        {!capturing && success && info?.traveler_info_submitted && (
          <p className="et-sans mt-6 text-center text-sm font-light" style={{ color: C.sub }} data-testid="pay-ti-done">Traveller details received — thank you.</p>
        )}

        {/* ---------- Formulario de pago ---------- */}
        {!capturing && !success && (
          <div className="space-y-10">
            {cancelled && (
              <div className="border px-5 py-4 text-sm font-light" style={{ borderColor: C.line, backgroundColor: C.sand, color: C.ink }} data-testid="pay-cancelled">
                Your payment was cancelled. You can choose an option below and try again whenever you're ready.
              </div>
            )}

            {shares.length > 0 && (
              <div data-testid="pay-who">
                <h2 className="et-serif text-2xl font-medium md:text-3xl">Who are you?</h2>
                <p className="et-sans mt-1 text-sm font-light" style={{ color: C.sub }}>This trip is split between travellers. Choose your name to see your share.</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {shares.map((sh) => {
                    const active = shareId === sh.id;
                    const done = sh.remaining_eur <= 0.01;
                    return (
                      <button key={sh.id} type="button" onClick={() => pickShare(sh.id)} data-testid={`pay-share-${sh.id}`}
                        className="flex items-center justify-between border px-5 py-4 text-left transition-colors hover:bg-[#F4F4F5]"
                        style={{ borderColor: active ? C.navy : C.line, backgroundColor: active ? "#FCFBF9" : C.surface }}>
                        <span>
                          <span className="et-sans block text-base font-medium" style={{ color: C.ink }}>{sh.name}</span>
                          <span className="et-sans text-xs font-light" style={{ color: C.sub }}>{done ? "Fully paid" : `Paid ${eur(sh.paid, cur)} of ${eur(sh.amount, cur)}`}</span>
                        </span>
                        <span className="et-serif text-xl font-medium" style={{ color: C.ink }}>{eur(sh.amount, cur)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Opciones */}
            <div style={{ opacity: shareOk ? 1 : 0.4, pointerEvents: shareOk ? "auto" : "none" }}>
              <h2 className="et-serif text-2xl font-medium md:text-3xl">How would you like to pay?</h2>
              {info?.days_until_departure != null && info.days_until_departure <= info.full_payment_days && (
                <p className="et-sans mt-1 text-sm font-light" style={{ color: C.sub }} data-testid="pay-full-only">Your trip departs within {info.full_payment_days} days, so full payment is required.</p>
              )}
              <div className="mt-5 grid gap-3">
                {options.map((o) => {
                  const active = option === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      disabled={!o.enabled}
                      onClick={() => setOption(o.key)}
                      data-testid={`pay-option-${o.key}`}
                      className={`flex items-center justify-between border px-5 py-4 text-left transition-colors ${o.enabled ? "cursor-pointer hover:bg-[#F4F4F5]" : "cursor-not-allowed opacity-40"}`}
                      style={{ borderColor: active ? C.navy : C.line, backgroundColor: active ? "#FCFBF9" : C.surface }}
                    >
                      <span className="flex items-center gap-3">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full border" style={{ borderColor: active ? C.navy : C.mute }}>
                          {active && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: C.navy }} />}
                        </span>
                        <span>
                          <span className="et-sans block text-base font-medium" style={{ color: C.ink }}>{o.label}</span>
                          {o.description && o.enabled && <span className="et-sans text-xs font-light" style={{ color: C.sub }}>{o.description}</span>}
                        </span>
                      </span>
                      {o.amount != null && <span className="et-serif text-xl font-medium" style={{ color: C.ink }}>{eur(o.amount, cur)}</span>}
                    </button>
                  );
                })}
              </div>

              {option === "custom" && (
                <div className="mt-4 max-w-xs" data-testid="pay-custom-wrap">
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>Amount to pay ({cur})</Label>
                  <Input
                    type="number" min="1" max={remaining} step="0.01" value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder={`Max ${eur(remaining, cur)}`}
                    className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-custom-input"
                  />
                  {custom && !customOk && <p className="et-sans mt-1.5 text-xs font-light text-red-600">Enter an amount between {eur(1, cur)} and {eur(remaining, cur)}.</p>}
                </div>
              )}
            </div>

            {/* Datos del viajero */}
            <div>
              <h2 className="et-serif text-2xl font-medium md:text-3xl">Traveller details</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>First name *</Label>
                  <Input value={passenger.first_name} onChange={(e) => setP("first_name", e.target.value)} className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-first-name" />
                </div>
                <div>
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>Last name *</Label>
                  <Input value={passenger.last_name} onChange={(e) => setP("last_name", e.target.value)} className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-last-name" />
                </div>
                <div>
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>Email *</Label>
                  <Input type="email" value={passenger.email} onChange={(e) => setP("email", e.target.value)} className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-email" />
                </div>
                <div>
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>Phone</Label>
                  <Input value={passenger.phone} onChange={(e) => setP("phone", e.target.value)} className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-phone" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="et-sans text-sm font-medium" style={{ color: C.sub }}>Notes (optional)</Label>
                  <Input value={passenger.notes} onChange={(e) => setP("notes", e.target.value)} className="mt-1.5 rounded-none border-[#D4D4D8]" data-testid="pay-notes" />
                </div>
              </div>
            </div>

            {/* T&C */}
            <label className="flex cursor-pointer items-start gap-3 border p-4" style={{ borderColor: tos ? C.navy : C.line, backgroundColor: C.surface }} data-testid="pay-tos">
              <input type="checkbox" checked={tos} onChange={(e) => setTos(e.target.checked)} className="mt-1 h-4 w-4" data-testid="pay-tos-checkbox" />
              <span className="et-sans text-sm font-light" style={{ color: C.sub }}>
                I accept the <a href="https://www.espiritutravel.com/terms" target="_blank" rel="noreferrer" className="underline" style={{ color: C.ink }}>Terms & Conditions</a> and the cancellation policy of Espíritu Travel. Deposits are non-refundable; the balance is due at least {info?.full_payment_days || 60} days before departure. Your acceptance is recorded with date and IP address.
              </span>
            </label>

            {/* CTA */}
            <div className="border p-6 md:p-8" style={{ borderColor: C.line, backgroundColor: C.navy }}>
              <div className="flex flex-col items-center gap-1 text-center text-white">
                <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">Amount due now</p>
                <p className="et-serif text-4xl font-medium" data-testid="pay-due">{eur(amountDue, cur)}</p>
              </div>
              <button
                onClick={submit}
                disabled={!canPay}
                data-testid="pay-submit"
                className="et-sans mt-5 flex w-full items-center justify-center gap-2 bg-white px-6 py-4 text-base font-semibold transition-colors hover:bg-[#F4EBD7] disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: C.navy }}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Continue to PayPal <ArrowRight className="h-5 w-5" /></>}
              </button>
              <p className="et-sans mt-3 text-center text-[11px] font-light text-white/50">
                You'll be redirected to PayPal to complete your payment securely.
                {cur === "USD" && info?.fx_rate ? ` Amounts in USD (EUR ${Number(info.total_eur).toLocaleString("en-US")} at 1 EUR = ${Number(info.fx_rate).toFixed(4)} USD).` : ""}
                {info?.fee_pct ? ` Includes ${info.fee_pct}% payment processing fee.` : ""}
              </p>
            </div>
          </div>
        )}

        {info?.consultant?.name && (
          <div className="mt-14 flex items-center gap-5 border p-6" style={{ borderColor: C.line, backgroundColor: C.surface }} data-testid="pay-consultant">
            {info.consultant.photo && <img src={imgUrl(info.consultant.photo)} alt={info.consultant.name} className="h-16 w-16 rounded-full object-cover" />}
            <div>
              <p className="et-sans text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.mute }}>Your travel specialist</p>
              <p className="et-serif mt-1 text-xl font-medium" style={{ color: C.ink }}>{info.consultant.name}</p>
              {info.consultant.email && <a href={`mailto:${info.consultant.email}`} className="et-sans text-sm font-light hover:opacity-70" style={{ color: C.sub }}>{info.consultant.email}</a>}
            </div>
          </div>
        )}
      </main>

      <footer className="px-6 py-10 text-center">
        <p className="et-serif text-xl font-light italic" style={{ color: C.sub }}>Feel part of the world</p>
        <p className="et-sans mt-2 text-[11px] font-light" style={{ color: C.mute }}>Espíritu Travel · Secure payment powered by PayPal</p>
      </footer>
    </div>
  );
}


function TravelerInfoForm({ token, numTravelers, open, setOpen, onDone }) {
  const n = Math.max(1, Number(numTravelers) || 1);
  const empty = { first_name: "", last_name: "", date_of_birth: "", nationality: "", passport_number: "", passport_expiry: "", dietary: "" };
  const [persons, setPersons] = useState(Array.from({ length: n }, () => ({ ...empty })));
  const [extra, setExtra] = useState({ emergency_contact_name: "", emergency_contact_phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const setPerson = (i, k, v) => setPersons((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const ok = persons.some((p) => p.first_name.trim() && p.last_name.trim());
  const submit = async () => {
    if (!ok) return;
    setSaving(true);
    try {
      await pub.post(`/pay/${token}/traveler-info`, { persons, ...extra });
      toast.success("Thank you — traveller details received.");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save traveller details."); }
    finally { setSaving(false); }
  };
  const field = (label, value, onChange, type = "text", testid) => (
    <div>
      <Label className="et-sans text-xs font-medium" style={{ color: C.sub }}>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-none border-[#D4D4D8]" data-testid={testid} />
    </div>
  );
  return (
    <div className="mt-6 border bg-white p-6 md:p-8" style={{ borderColor: C.line }} data-testid="pay-traveler-info">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="et-serif text-2xl font-medium">Traveller details</h3>
          <p className="et-sans mt-1 text-sm font-light" style={{ color: C.sub }}>We need the passport details of every traveller to confirm your bookings.</p>
        </div>
        {!open && <button type="button" onClick={() => setOpen(true)} className="et-sans px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: C.navy }} data-testid="pay-ti-open">Add traveller details</button>}
      </div>
      {open && (
        <div className="mt-6 space-y-6">
          {persons.map((p, i) => (
            <div key={i} className="border p-4" style={{ borderColor: C.line }} data-testid={`pay-ti-person-${i}`}>
              <p className="et-sans mb-3 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.mute }}>Traveller {i + 1}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {field("First name *", p.first_name, (v) => setPerson(i, "first_name", v), "text", `pay-ti-first-${i}`)}
                {field("Last name *", p.last_name, (v) => setPerson(i, "last_name", v), "text", `pay-ti-last-${i}`)}
                {field("Date of birth", p.date_of_birth, (v) => setPerson(i, "date_of_birth", v), "date")}
                {field("Nationality", p.nationality, (v) => setPerson(i, "nationality", v))}
                {field("Passport number", p.passport_number, (v) => setPerson(i, "passport_number", v), "text", `pay-ti-passport-${i}`)}
                {field("Passport expiry", p.passport_expiry, (v) => setPerson(i, "passport_expiry", v), "date")}
                <div className="sm:col-span-2">{field("Dietary requirements / allergies", p.dietary, (v) => setPerson(i, "dietary", v))}</div>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <button type="button" onClick={() => setPersons([...persons, { ...empty }])} className="et-sans text-sm underline" style={{ color: C.sub }} data-testid="pay-ti-add">+ Add another traveller</button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {field("Emergency contact name", extra.emergency_contact_name, (v) => setExtra({ ...extra, emergency_contact_name: v }))}
            {field("Emergency contact phone", extra.emergency_contact_phone, (v) => setExtra({ ...extra, emergency_contact_phone: v }))}
            <div className="sm:col-span-2">{field("Home address", extra.address, (v) => setExtra({ ...extra, address: v }))}</div>
            <div className="sm:col-span-2">{field("Anything else we should know?", extra.notes, (v) => setExtra({ ...extra, notes: v }))}</div>
          </div>
          <button type="button" onClick={submit} disabled={!ok || saving} className="et-sans flex w-full items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white disabled:opacity-50" style={{ backgroundColor: C.navy }} data-testid="pay-ti-submit">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send traveller details"}
          </button>
        </div>
      )}
    </div>
  );
}
